import React from 'react';
import { cx } from '../cx';

// D-07 §21.2 — SidePanel (.side-panel). 접이식 보조 패널.
// 닫히면 폭이 0이 되고 토글 버튼만 가장자리에 남는다(내용은 언마운트).
// 기본 폭 308px = --layout-side-panel.
export interface SidePanelProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  open: boolean;
  /** 지정하면 패널 바깥 가장자리에 원형 토글 버튼을 노출한다 */
  onToggle?: () => void;
  /** 패널이 붙는 쪽. 기본 'right' */
  side?: 'right' | 'left';
  /** 지정하면 패널 상단에 헤더 줄을 그린다 */
  title?: React.ReactNode;
  /** 패널 폭(px). 기본값은 --layout-side-panel(308) */
  width?: number;
  /** 토글 버튼의 접근성 레이블. 기본 '패널' */
  label?: string;
}

export function SidePanel({
  open,
  onToggle,
  side = 'right',
  title,
  width,
  label = '패널',
  className = '',
  style,
  children,
  ...props
}: SidePanelProps) {
  const isRight = side === 'right';
  const openWidth = width != null ? `${width}px` : 'var(--layout-side-panel)';
  // 이중 셰브런은 "닫으면 패널이 사라질 방향"을 가리킨다 — 오른쪽 패널이 열려 있으면 '››'.
  const flip = open !== isRight;

  return (
    <div
      className={cx('side-panel', className)}
      style={{ width: open ? openWidth : 0, ...style }}
      {...props}
    >
      {onToggle && (
        <button
          type="button"
          onClick={onToggle}
          aria-label={`${label} ${open ? '닫기' : '열기'}`}
          aria-expanded={open}
          className={cx('side-panel-toggle', isRight ? 'side-panel-toggle-right' : 'side-panel-toggle-left')}
        >
          {/* viewBox 16 은 저작 그리드 — Figma DS `Fold`(2261:337) 의 셰브런 3×8 · 획 2 · 간격 2 를
              그대로 옮긴 좌표다. 렌더 크기만 15 로 내린다(글리프는 항상 박스의 절반). */}
          <svg
            width="15" height="15" viewBox="0 0 16 16" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            className={cx('side-panel-chevron', flip && 'side-panel-chevron-flip')}
          >
            <polyline points="4 4 7 8 4 12" />
            <polyline points="9 4 12 8 9 12" />
          </svg>
        </button>
      )}

      {open && (
        <div
          className={cx('side-panel-inner', isRight ? 'side-panel-inner-right' : 'side-panel-inner-left')}
          style={{ width: openWidth }}
        >
          {title != null && <div className="side-panel-head">{title}</div>}
          <div className="side-panel-body">{children}</div>
        </div>
      )}
    </div>
  );
}
